import joi from 'joi';

import {
  pathLookupEdgeAttributes,
  protocolEdgeAttributes,
} from '../edges/constants';
import type {
  PathLookupEdgeSettings,
  TopologyEdgeSettings,
} from './EdgeSettings';

export const graphEdgeSettingsStyleSchema = joi.object().keys({
  color: joi.string().regex(/^#?[a-fA-F0-9]{3}(?:[a-fA-F0-9]{3})?$/), // Short and full RGB format is allowed
  pattern: joi.valid('dashed', 'dotted', 'solid'),
  thicknessThresholds: joi
    .array()
    .ordered(
      joi.number().min(1).required(),
      joi.number().min(joi.ref('0')).required(),
      joi.number().min(joi.ref('1')).required(),
    )
    .unique(),
});

/**
 * Generate schema for single protocol / path lookup edge type
 */
const generateEdgeSettingsSchema = (
  type: 'protocol' | 'pathLookupEdge',
  protocolName: string,
  labels: string[],
): joi.ObjectSchema<TopologyEdgeSettings | PathLookupEdgeSettings> =>
  joi.object().keys({
    id: joi.string().guid().required(),
    name: joi.valid(protocolName).required(),
    grouped: joi.boolean().required(),
    labels: joi
      .array()
      .items(joi.valid(...labels))
      .unique()
      .required(),
    labelsOptions: joi.object().keys({
      wrapCenterLabel: joi.boolean(),
      wrapLinecapLabel: joi.boolean(),
    }),
    style: graphEdgeSettingsStyleSchema.required(),
    type: joi.valid(type).required(),
    visible: joi.boolean().required(),
  });

/**
 * Generate schema for all protocol or all path lookup edge types
 */
export const generateEdgeSettingsSchemas = (
  type: 'protocol' | 'pathLookupEdge',
  forkEdgeSettingsSchema: (
    s: joi.ObjectSchema<TopologyEdgeSettings | PathLookupEdgeSettings>,
  ) => joi.ObjectSchema<TopologyEdgeSettings | PathLookupEdgeSettings> = (s) =>
    s,
): joi.ObjectSchema<TopologyEdgeSettings | PathLookupEdgeSettings>[] =>
  Object.entries(
    {
      protocol: protocolEdgeAttributes,
      pathLookupEdge: pathLookupEdgeAttributes,
    }[type],
  )
    .filter(([, { allowInGraphSettings }]) => allowInGraphSettings)
    .map(([protocolName, data]) =>
      forkEdgeSettingsSchema(
        generateEdgeSettingsSchema(type, protocolName, [
          ...data.labels.center,
          ...data.labels.linecap,
        ]),
      ),
    );
